<template>
  <teleport to="body" :disabled="!appendToBody">
    <transition
      name="dialog-fade"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
    >
      <el-overlay
        v-show="visible"
        custom-mask-event
        :mask="modal"
        :overlay-class="modalClass"
        :z-index="zIndex"
      >
        <div
          role="dialog"
          aria-modal="true"
          :aria-label="title || undefined"
          :aria-labelledby="!title ? titleId : undefined"
          :aria-describedby="bodyId"
          :class="[`${ns.namespace.value}-overlay-dialog-header`, modalClass]"
          :style="overlayDialogStyle"
          @click="overlayEvent.onClick"
          @mousedown="overlayEvent.onMousedown"
          @mouseup="overlayEvent.onMouseup"
        >
          <el-focus-trap
            loop
            :trapped="visible"
            focus-start-el="container"
            @focus-after-trapped="onOpenAutoFocus"
            @focus-after-released="onCloseAutoFocus"
            @focusout-prevented="onFocusoutPrevented"
            @release-requested="onCloseRequested"
          >
            <div :class="`${ns.namespace.value}-dialog-header-preview`">
              <el-page-header @back="handleClose">
                <template #content>
                  <span class="text-large font-600 mr-3"> {{ title }} </span>
                </template>
              </el-page-header>
              <div :class="`${ns.namespace.value}-dialog-header-option`">
                <slot name="footer" />
                <el-button
                  v-if="props.noCancelBtn"
                  :is-disabled="true"
                  @click="handleClose"
                  >取 消</el-button
                >
                <slot name="footer-right" />
              </div>
            </div>
            <div :class="`${ns.namespace.value}-dialog-header-preview-main`">
              <slot />
            </div>
          </el-focus-trap>
        </div>
      </el-overlay>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, provide, ref, useSlots } from 'vue'
import { ElOverlay } from '@pit-element-plus/components/overlay'
import ElFocusTrap from '@pit-element-plus/components/focus-trap'
import ElButton from '@pit-element-plus/components/button'
import ElPageHeader from '@pit-element-plus/components/page-header'
import {
  useDeprecated,
  useNamespace,
  useSameTarget,
} from '@pit-element-plus/hooks'
import { dialogHeaderInjectionKey } from './constants'
import { dialogHeaderEmits, dialogHeaderProps } from './dialog-header'
import { useDialogHeader } from './use-dialog-header'

defineOptions({
  name: 'ElDialogHeader',
  inheritAttrs: false,
})

const props = defineProps(dialogHeaderProps)
defineEmits(dialogHeaderEmits)
const slots = useSlots()

useDeprecated(
  {
    scope: 'el-dialog',
    from: 'the title slot',
    replacement: 'the header slot',
    version: '3.0.0',
    ref: 'https://element-plus.org/en-US/component/dialog.html#slots',
  },
  computed(() => !!slots.title)
)

useDeprecated(
  {
    scope: 'el-dialog',
    from: 'custom-class',
    replacement: 'class',
    version: '2.3.0',
    ref: 'https://element-plus.org/en-US/component/dialog.html#attributes',
    type: 'Attribute',
  },
  computed(() => !!props.customClass)
)

const ns = useNamespace('dialog')
const dialogRef = ref<HTMLElement>()
const headerRef = ref<HTMLElement>()
const dialogContentRef = ref()

const {
  visible,
  titleId,
  bodyId,
  style,
  overlayDialogStyle,
  rendered,
  zIndex,
  afterEnter,
  afterLeave,
  beforeLeave,
  handleClose,
  onModalClick,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onCloseRequested,
  onFocusoutPrevented,
} = useDialogHeader(props, dialogRef)

provide(dialogHeaderInjectionKey, {
  dialogRef,
  headerRef,
  bodyId,
  ns,
  rendered,
  style,
})

const overlayEvent = useSameTarget(onModalClick)

defineExpose({
  /** @description whether the dialog is visible */
  visible,
  dialogContentRef,
})
</script>
